import React, { useEffect, useRef, useState } from "react";
import { apiAxios } from "../utils/apiAxios";
import { notificationQueue } from "../hooks/notificationQueue";
import Swal from "sweetalert2";
const API_BASE = process.env.REACT_APP_API_BASE_URL || "";
type Props = {
  apiNotificationUrl: string; // This should be a RELATIVE path like "/Notification/manager-notifications"
  userRole: string | null;
  isGloballyMuted?: boolean;
  onMuteToggle?: (muted: boolean) => void;
};

const NotificationBell: React.FC<Props> = ({
  apiNotificationUrl,
  userRole,
  isGloballyMuted = false,
  onMuteToggle,
}) => {
  const [notifications, setNotifications] = useState<any[]>([]);
  const attentionAudioRef = useRef<HTMLAudioElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isMuted, setIsMuted] = useState(isGloballyMuted);
  const isAudioUnlocked = useRef(false);
  // Refs
  const dropdownRef = useRef<HTMLDivElement>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const prevUnreadCount = useRef<number>(0);
  const notificationCache = useRef<Set<string>>(new Set());
  // const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isMounted = useRef<boolean>(true);
  const fetchInProgress = useRef<boolean>(false);

  const isSoundAllowed =
    userRole === "Designer" ||
    userRole === "Central Hub" ||
    userRole === "Milling Hub" ||
    userRole === "Manager" ||
    userRole === "Dentist";

  // Log props for debugging
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {

    }
  }, [apiNotificationUrl, userRole, isGloballyMuted]);

  // Initialize audio
  useEffect(() => {
    audioRef.current = new Audio("/Notificationsound/notification.mp3");
    audioRef.current.volume = 1;
    audioRef.current.load();
    attentionAudioRef.current = new Audio("/Notificationsound/alert.mp3"); // 🔥 new sound
    attentionAudioRef.current.volume = 1;
    attentionAudioRef.current.load();

    return () => {
      audioRef.current?.pause();
      attentionAudioRef.current?.pause();

      audioRef.current = null;
      attentionAudioRef.current = null;
    };
  }, []);

  useEffect(() => {
    const unlockAudio = () => {
      const audio = audioRef.current;
      const attentionAudio = attentionAudioRef.current;

      if (audio) {
        audio.muted = true;
        audio.play()
          .then(() => {
            audio.pause();
            audio.currentTime = 0;
            audio.muted = false;
            isAudioUnlocked.current = true;
          })
          .catch(() => { });
      }

      if (attentionAudio) {
        attentionAudio.muted = true;
        attentionAudio.play()
          .then(() => {
            attentionAudio.pause();
            attentionAudio.currentTime = 0;
            attentionAudio.muted = false;
          })
          .catch(() => { });
      }

      document.removeEventListener("click", unlockAudio);
    };

    document.addEventListener("click", unlockAudio);

    return () => {
      document.removeEventListener("click", unlockAudio);
    };
  }, []);

  const playNormalSound = () => {
    const audio = audioRef.current;
    if (!audio || isMuted || !isSoundAllowed) return;

    audio.currentTime = 0;
    audio.play().catch(() => { });
  };

  const playAttentionSound = () => {

    const audio = attentionAudioRef.current;
    if (!audio) return;
    if (isMuted) return;
    if (!isSoundAllowed) return;

    // 🔥 THIS IS MISSING
    if (!isAudioUnlocked.current) {
      console.warn("⛔ Audio not unlocked yet");
      return;
    }

    audio.currentTime = 0;

    audio.play()
      .then(() => console.warn("✅ Attention sound played"))
      .catch((err) => console.error("❌ Play failed:", err));
  };

  const playSound = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isMuted) return;
    if (!isSoundAllowed) return;

    audio.currentTime = 0;

    const playPromise = audio.play();
    if (playPromise !== undefined) {
      playPromise.catch((error) => {
        if (error.name === 'NotAllowedError') {
          const unlockAudio = () => {
            audio.play().catch(() => { });
            document.removeEventListener('click', unlockAudio);
          };
          document.addEventListener('click', unlockAudio, { once: true });
        }
      });
    }
  };

  const toggleMute = () => {
    const newMutedState = !isMuted;
    setIsMuted(newMutedState);
    onMuteToggle?.(newMutedState);

    if (!newMutedState) {
      setTimeout(playSound, 100);
    }
  };

  // Fetch notifications - Handle your specific response structure
  const fetchNotifications = async (playSound = true) => {
    if (fetchInProgress.current) {
      return;
    }

    if (!apiNotificationUrl || apiNotificationUrl.includes('undefined')) {
      console.error("Invalid notification URL:", apiNotificationUrl);
      return;
    }

    fetchInProgress.current = true;

    try {
      setLoading(true);

      const response = await apiAxios.get(`${API_BASE}/Notification/manager-notifications`);

      // Handle your specific response structure from [HttpGet("manager-notifications")]
      // Your API returns a ResponseDto with data property
      let notificationsData = [];

      if (response.data?.isSuccess && response.data?.dataOne) {
        // Your API returns { isSuccess: true, data: [...], ... }
        notificationsData = response.data.dataOne;
      } else if (Array.isArray(response.data.dataOne)) {
        notificationsData = response.data.dataOne;
      } else if (response.data?.dataOne) {
        notificationsData = response.data.dataOne;
      } else if (response.data?.notifications) {
        notificationsData = response.data.notifications;
      } else {
        notificationsData = response.data.dataOne || [];
      }

      notificationsData = Array.isArray(notificationsData) ? notificationsData : [];


      // Find new unread notifications
      const newUnreadNotifications = notificationsData.filter(
        (n: any) => !n.isRead && !notificationCache.current.has(`${n.notificationID}`)
      );

      // Play sound for new notifications
      if (newUnreadNotifications.length > 0) {
        newUnreadNotifications.forEach((n: any) => {
          notificationCache.current.add(`${n.notificationID}`);
        });
        //playSound();
      }
      const newAlertNotifications = notificationsData.filter(
        (n: any) => n.notificationType === "DesignerReassigned" || n.notificationType === "ScanRejectedByDesigner"
      );
      const hasAttention = newUnreadNotifications.some(
        (n: any) => n.notificationType === "DesignerReassigned" || n.notificationType === "ScanRejectedByDesigner" // 🔥 example types
      );
      const criticalNotifications = notificationsData.filter(
        (n: any) =>
          n.isCritical &&
          !notificationCache.current.has(`critical-${n.notificationID}`)
      );

      if (newAlertNotifications.length > 0) {
        newAlertNotifications.forEach((n: any) => {
          notificationQueue.add({
            id: n.id?.toString() || `${Date.now()}-${Math.random()}`,
            title: n.title || "🚨 Urgent Alert",
            body: n.message || n.description || "You have an urgent notification",
          });
        });
      }
      if (playSound) {
        if (hasAttention) {
          playAttentionSound(); // 🔥 priority sound
        } else {
          playNormalSound(); // 🔔 normal sound
        }
        // 🔥 NEW: Critical popup
        if (criticalNotifications.length > 0) {
          showCriticalAlertsSequentially(criticalNotifications);
        }
      }
      prevUnreadCount.current = notificationsData.filter((n: any) => !n.isRead).length;

      if (isMounted.current) {

        setNotifications(notificationsData);
      }

    } catch (error) {
      console.error("Notification fetch error:", error);
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
      fetchInProgress.current = false;
    }
  };

  const showCriticalAlertsSequentially = async (criticalNotifications: any[]) => {
    const user = JSON.parse(sessionStorage.getItem("user") || "{}")
    if (user.roleName == "Central Hub" || user.roleName == "Manager")
      for (const n of criticalNotifications) {
        // prevent duplicate popup
        const key = `critical-${n.notificationID}`;
        if (notificationCache.current.has(key)) continue;

        notificationCache.current.add(key);

      //   await Swal.fire({
      //     title: n.title || "🚨 Critical Alert",
      //     text: n.message || n.description || "Important notification",
      //     icon: "warning",
      //     confirmButtonText: "View",
      //     allowOutsideClick: false,
      //     allowEscapeKey: false,
      //     didOpen: (popup) => {
      //       const iconEl = popup.querySelector('.swal2-icon');
      //       if (iconEl) {
      //         iconEl.innerHTML = `
      //   <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
      //     style="width:24px;height:24px;">
      //     <path d="M12 9v4M12 17h.01M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
      //       stroke="#E24B4A" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
      //   </svg>
      // `;
      //       }
      //     },

      //     customClass: {
      //       popup: "my-popupsla",
      //       title: "my-titlesla",
      //       htmlContainer: "my-textsla",
      //       confirmButton: "my-confirm-btnsla btn py-2 px-3  btn-primary btn-primary",
      //     },

      //     buttonsStyling: false // IMPORTANT when using custom classes
      //   });

        // after user clicks
        markAsSeen(n.notificationID);
      }
  };

  // Setup polling
  useEffect(() => {
    isMounted.current = true;

    if (apiNotificationUrl && !apiNotificationUrl.includes('undefined')) {
      // Initial fetch
      fetchNotifications();

      // Setup interval
      // intervalRef.current = setInterval(fetchNotifications, 10000);

      // Cleanup
      return () => {
        isMounted.current = false;
        // if (intervalRef.current) {
        //   clearInterval(intervalRef.current);
        //   intervalRef.current = null;
        // }
      };
    }
  }, [apiNotificationUrl]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Mark as read
  const markAsRead = async (notificationId: number) => {
    try {
      await apiAxios.post(`${API_BASE}/Notification/mark-notification-read/${notificationId}`);

      setNotifications((prev) =>
        prev.map((n) =>
          n.notificationID === notificationId ? { ...n, isRead: true } : n
        )
      );

      notificationCache.current.delete(`${notificationId}`);
      prevUnreadCount.current = Math.max(0, prevUnreadCount.current - 1);

    } catch (error) {
      console.error("Mark as read error:", error);
    }
  };

  const markAsSeen = async (notificationId: number) => {
    try {
      await apiAxios.post(
        `${API_BASE}/Notification/mark-notification-seen/${notificationId}`
      );
      // Update UI state
      setNotifications((prev) =>
        prev.map((n) =>
          n.notificationID === notificationId
            ? { ...n, isSeen: true } // 👈 UI-only flag
            : n
        )
      );

      // Clear cache
      notificationCache.current.delete(`${notificationId}`);

      // Update unread count
      prevUnreadCount.current = Math.max(0, prevUnreadCount.current - 1);
    } catch (error) {
      console.error("Mark as seen error:", error);
    }
  };

  const handleRefresh = () => {
    fetchNotifications();
  };

  useEffect(() => {
    const handleBellNotification = (event: any) => {
      const newNotification = event.detail;

      setNotifications(prev => {
        // ✅ prevent duplicate
        if (prev.some(n => n.notificationID === newNotification.notificationID)) {
          return prev;
        }

        // ✅ add to top
        return [
          {
            ...newNotification,
            isRead: false
          },
          ...prev
        ];
      });

      // 🔔 optional sound
      playSound();
    };

    window.addEventListener("bell_notification", handleBellNotification);

    return () => {
      window.removeEventListener("bell_notification", handleBellNotification);
    };
  }, []);

  const unreadCount = notifications.filter(n => !n.isRead).length;

  return (
    <div className="position-relative" ref={dropdownRef}>
      {/* Bell Icon */}
      <div
        className="bellmain-div position-relative"
        onClick={() => {
          if (!isOpen) {
            fetchNotifications(false);
          }
          setIsOpen(!isOpen);
        }}
        style={{ cursor: "pointer" }}
      >
        <i className="bi bi-bell-fill fs-5" />

        {unreadCount > 0 && (
          <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
            {unreadCount}
          </span>
        )}
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div
          className="notification-dropdown"
          style={{
            width: "320px",
            maxHeight: "380px",
            background: "#fff",
            borderRadius: "8px",
            boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
            display: "flex",
            flexDirection: "column",
            position: "absolute",
            right: 0,
            top: "100%",
            zIndex: 1000,
          }}
        >
          {/* Header */}
          <div className="p-2 border-bottom bg-light">
            <div className="d-flex justify-content-between align-items-center">
              <h6 className="mb-0">
                Notifications
                {unreadCount > 0 && (
                  <span className="badge bg-danger ms-2">{unreadCount}</span>
                )}
              </h6>
              <button
                className="btn btn-sm btn-link p-0"
                onClick={handleRefresh}
                disabled={loading}
                title="Refresh"
              >
                <i className={`bi ${loading ? 'bi-arrow-repeat spin' : 'bi-arrow-clockwise'}`} />
              </button>
            </div>
          </div>

          {/* Scroll Area */}
          <div style={{ overflowY: "auto", overflowX: "hidden", maxHeight: "300px" }}>
            {loading && notifications.length === 0 && (
              <div className="p-3 text-center">
                <div className="spinner-border spinner-border-sm text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <div className="mt-2 small">Loading notifications...</div>
              </div>
            )}

            {!loading && notifications.length === 0 && (
              <div className="p-3 text-center text-muted">
                <i className="bi bi-bell-slash fs-1 d-block mb-2"></i>
                <span className="small">No notifications</span>
              </div>
            )}

            {notifications.map((n) => (
              <div
                key={n.notificationID}
                onClick={() => !n.isRead && markAsSeen(n.notificationID)}
                className={`notification-item  border-bottom ${!n.isRead ? "bg-light" : ""
                  }`}
                style={{ cursor: "pointer" }}
              >
                <div className="d-flex gap-2">
                  <i className={`bi ${!n.isRead ? 'bi-bell-fill text-primary' : 'bi-bell-slash text-secondary'
                    }`} />
                  <div className="main-badge-and-content">
                    <div className="strong-and-spandiv">
                      <strong className={!n.isRead ? "fw-semibold" : "fw-normal"}>
                        {n.title || "Notification"}
                      </strong>



                      <div className="small text-muted">
                        {n.message || n.description || "No message"}
                      </div>
                      {n.createdDate && (
                        <div className="small text-muted mt-1">
                          <i className="bi bi-clock me-1"></i>
                          {new Date(n.createdDate).toLocaleString('en-GB')}
                        </div>
                      )}
                    </div>
                    <div className="btn-badge-div">
                      {!n.isRead && (
                        <div className="badge bg-primary rounded-pill mx-3">New</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="d-flex justify-content-between align-items-center p-2 border-top bg-white">
            {/* {isSoundAllowed && (
              <button
                className="btn btn-sm btn-secondary"
                onClick={toggleMute}
                title={isMuted ? "Unmute" : "Mute"}
              >
                <i className={`bi ${isMuted ? "bi-volume-mute-fill" : "bi-volume-up-fill"}`} />
                <span className="ms-1 small">{isMuted ? "Muted" : "Unmuted"}</span>
              </button>
            )} */}

          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;